/**
 * Loss Detail Drawer Component
 *
 * Side drawer for viewing loss details and product history.
 * @see Sprint 13 - Pertes
 * @see US-13.7 - Drawer Detail Perte
 */

import { Calendar, User, AlertTriangle, TrendingDown, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Drawer, Badge, Skeleton, Button } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes, formatQuantity } from '@/lib/format';
import { useLoss, useCancelLoss } from '../hooks/useLosses';
import type { LossType } from '@bstock/shared';
import {
  LossReason,
  LOSS_REASON_LABELS,
  LOSS_REASON_COLORS,
  LOSS_TYPE_LABELS,
  LOSS_TYPE_COLORS,
} from '@bstock/shared';

interface LossDetailDrawerProps {
  lossId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showFinancials?: boolean;
  canCancel?: boolean;
}

/**
 * Map loss reason to badge variant
 */
function getReasonVariant(reason: LossReason): 'success' | 'warning' | 'danger' | 'neutral' | 'primary' | 'info' {
  return LOSS_REASON_COLORS[reason] ?? 'neutral';
}

export function LossDetailDrawer({
  lossId,
  open,
  onOpenChange,
  showFinancials = false,
  canCancel = false,
}: LossDetailDrawerProps) {
  const navigate = useNavigate();
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();
  const { data: loss, isLoading } = useLoss(open ? lossId : null);
  const cancelMutation = useCancelLoss();

  const handleCancel = async () => {
    if (lossId && window.confirm('Voulez-vous annuler cette perte ?')) {
      await cancelMutation.mutateAsync(lossId);
      onOpenChange(false);
    }
  };

  const handleViewProduct = () => {
    if (loss) {
      navigate(`/products/raw/${loss.rawProductId}`);
    }
  };

  const footer = (
    <div className="flex justify-between items-center">
      {canCancel && loss && !loss.isCancelled ? (
        <Button
          variant="ghost"
          size="sm"
          className="text-danger-500 hover:bg-danger-50"
          onClick={handleCancel}
          disabled={cancelMutation.isPending}
          isLoading={cancelMutation.isPending}
        >
          Annuler la perte
        </Button>
      ) : (
        <div />
      )}
      <Button variant="secondary" onClick={() => onOpenChange(false)}>
        Fermer
      </Button>
    </div>
  );

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Detail de la perte"
      description={loss?.id?.substring(0, 8) ?? ''}
      size="large"
      footer={footer}
    >
      <div>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-48" />
            <Skeleton className="h-32" />
          </div>
        ) : loss ? (
          <div className="space-y-6">
            {/* Header with badges */}
            <div className="flex items-center gap-2">
              <Badge variant={LOSS_TYPE_COLORS[(loss.lossType ?? 'RAW') as LossType]}>
                {LOSS_TYPE_LABELS[(loss.lossType ?? 'RAW') as LossType]}
              </Badge>
              <Badge variant={getReasonVariant(loss.lossReason)} >
                {LOSS_REASON_LABELS[loss.lossReason]}
              </Badge>
              {loss.isCancelled && (
                <Badge variant="neutral">Annulee</Badge>
              )}
            </div>

            {/* General information */}
            <div>
              <h3 className="text-sm font-semibold text-dark-500 uppercase tracking-wider mb-4">
                Informations generales
              </h3>
              <div className="bg-neutral-50 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 text-neutral-500">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">Date & heure</span>
                  </div>
                  <span className="text-sm font-medium text-dark-500">
                    {formatDateShort(loss.eventDate)}
                  </span>
                </div>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 text-neutral-500">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Motif</span>
                  </div>
                  <span className="text-sm font-medium text-dark-500">
                    {LOSS_REASON_LABELS[loss.lossReason]}
                  </span>
                </div>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 text-neutral-500">
                    <User className="w-4 h-4" />
                    <span className="text-sm">Utilisateur</span>
                  </div>
                  <span className="text-sm font-medium text-dark-500">
                    {loss.createdByUser
                      ? `${loss.createdByUser.firstName} ${loss.createdByUser.lastName}`
                      : '-'}
                  </span>
                </div>
              </div>
            </div>

            {/* Product information */}
            <div>
              <h3 className="text-sm font-semibold text-dark-500 uppercase tracking-wider mb-4">
                Produit concerne
              </h3>
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-base font-semibold text-dark-500">
                      {loss.rawProduct.name}
                    </div>
                    <div className="text-xs text-neutral-400 mt-1 font-mono">
                      SKU: {loss.rawProduct.sku}
                    </div>
                    <div className="text-xs text-neutral-400 mt-0.5">
                      Categorie: {loss.rawProduct.category.name}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleViewProduct}
                    className="text-neutral-500"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Voir fiche
                  </Button>
                </div>
              </div>
            </div>

            {/* Loss detail */}
            <div>
              <h3 className="text-sm font-semibold text-dark-500 uppercase tracking-wider mb-4">
                Detail de la perte
              </h3>
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                <div className={showFinancials ? 'grid grid-cols-2 gap-4 mb-4' : ''}>
                  <div>
                    <div className="text-xs text-neutral-500 mb-1">Quantite perdue</div>
                    <div className="text-2xl font-semibold text-danger-600">
                      -{formatQuantity(loss.quantity)}{' '}
                      <span className="text-base font-normal">{loss.rawProduct.recipeUnit.symbol}</span>
                    </div>
                  </div>
                  {showFinancials && (
                    <div>
                      <div className="text-xs text-neutral-500 mb-1">Prix unitaire (PMP)</div>
                      <div className="text-xl font-semibold text-dark-500">
                        {formatCentimes(loss.unitCost, currency)}/{loss.rawProduct.recipeUnit.symbol}
                      </div>
                    </div>
                  )}
                </div>
                {showFinancials && (
                  <div className="pt-3 border-t border-danger-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-dark-500">
                        Valorisation totale
                      </span>
                      <span className="text-2xl font-bold text-danger-600">
                        -{formatCentimes(loss.totalValue, currency)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes/Comment */}
            {loss.notes && (
              <div>
                <h3 className="text-sm font-semibold text-dark-500 uppercase tracking-wider mb-4">
                  Commentaire
                </h3>
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <p className="text-sm text-dark-500 leading-relaxed">
                    {loss.notes}
                  </p>
                </div>
              </div>
            )}

            {/* Product loss history */}
            {loss.productLossHistory && loss.productLossHistory.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-dark-500 uppercase tracking-wider">
                    Historique pertes du produit
                  </h3>
                  <TrendingDown className="w-4 h-4 text-danger-500" />
                </div>
                <div className="space-y-2">
                  {loss.productLossHistory.map((item) => (
                    <div
                      key={item.id}
                      className={`bg-white border rounded-lg p-3 ${
                        item.id === loss.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-neutral-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-mono text-neutral-400">
                          {item.id.substring(0, 8)}
                        </span>
                        <span className="text-xs text-neutral-400">
                          {formatDateShort(item.eventDate)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-danger-600">
                            -{formatQuantity(item.quantity)} {loss.rawProduct.recipeUnit.symbol}
                          </span>
                          <Badge variant={getReasonVariant(item.lossReason)}>
                            {LOSS_REASON_LABELS[item.lossReason]}
                          </Badge>
                        </div>
                        {showFinancials && (
                          <span className="text-sm font-semibold text-danger-600">
                            -{formatCentimes(item.totalValue, currency)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total 30 days */}
                {showFinancials && loss.productLossTotal30d && (
                  <div className="mt-3 pt-3 border-t border-neutral-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-dark-500">
                        Total pertes produit (30 jours)
                      </span>
                      <span className="font-bold text-danger-600">
                        -{formatCentimes(loss.productLossTotal30d.totalValue, currency)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-neutral-500 py-8">
            Perte non trouvee
          </div>
        )}
      </div>
    </Drawer>
  );
}
